import {
  Box,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  useColorModeValue,
} from '@chakra-ui/react';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { IconButton } from '@main/complykit/components/icon-button';
import { Switch } from '@main/complykit/components/switch';
import { NavigateOptions, useRouter } from '@tanstack/react-router';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface HeaderProps {
  logo: React.ReactNode;
  onSidebarToggle: () => void;
  menuContent?: React.ReactNode;
  menuMainItem?: React.ReactNode;
  menuItems?: HeaderMenuItem[];
  notificationCenter?: React.JSXElementConstructor<object>;
  taskSidebar?: React.JSXElementConstructor<object>;
  /**
   * When true, the floating chrome shrinks to a tighter strip (48px height,
   * centered alignment). Use on dense, full-height surfaces like /inbox where
   * the default 64px floating band leaves too much empty space above content.
   * Mobile behavior is unaffected.
   */
  compact?: boolean;
}

export interface HeaderMenuItem extends NavigateOptions, React.ComponentProps<'button'> {
  label: string;
  icon: React.ElementType;
  showSwitch?: boolean;
  isSwitchChecked?: boolean;
  onSwitchChange?: () => void;
}

export const Header = ({
  logo,
  onSidebarToggle,
  menuContent,
  menuMainItem,
  menuItems,
  notificationCenter: NotificationCenter,
  taskSidebar: TaskSidebar,
  compact,
}: HeaderProps) => {
  const { t } = useTranslation('ui');
  const { navigate } = useRouter();
  const menuTextColor = useColorModeValue('gray.600', 'gray.300');
  const shadowValue = useColorModeValue('lg', 'dark-lg');
  const bgColorMobile = useColorModeValue('white', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hasMenuItems = !!menuMainItem || !!menuItems?.length;
  const showMenu = hasMenuItems || !!menuContent;

  return (
    <Flex
      px={{ base: 4, md: 8 }}
      height={{ base: '16', md: compact ? '12' : '16' }}
      alignItems={{ base: 'center', md: compact ? 'center' : 'flex-end' }}
      position={{ base: 'relative', md: 'absolute' }}
      zIndex={39}
      bg={{ base: bgColorMobile, md: 'transparent' }}
      borderBottomWidth={{ base: '1px', md: '0' }}
      borderBottomColor={borderColor}
      justifyContent={{ base: 'space-between', md: 'flex-end' }}
      top={0}
      right={0}
    >
      <Box display={{ base: 'flex', md: 'none' }}>
        <IconButton icon={Bars3Icon} label={t('layout.openMenu')} onPress={onSidebarToggle} />
      </Box>

      <Box textAlign="center" maxW={32} display={{ base: 'flex', md: 'none' }}>
        {logo}
      </Box>

      <Flex gap={1}>
        {TaskSidebar && <TaskSidebar />}
        {NotificationCenter && <NotificationCenter />}
        {showMenu && (
          <Menu computePositionOnMount>
            <MenuButton
              ml={2}
              transition="all 0.3s"
              _focus={{ boxShadow: 'none' }}
              data-testid="header-account-button"
            >
              {menuContent}
            </MenuButton>
            {hasMenuItems && (
              <MenuList alignItems="center" shadow={shadowValue}>
                {menuMainItem}
                {!!menuMainItem && !!menuItems?.length && <MenuDivider />}
                {menuItems?.map((menu) => {
                  const handleMenuItemClick = (event: React.MouseEvent<HTMLButtonElement>) => {
                    if (menu.showSwitch) {
                      menu.onSwitchChange?.();
                      return;
                    }

                    if (menu.onClick) {
                      menu.onClick(event);
                      return;
                    }

                    if (menu.to) {
                      void navigate(menu);
                    }
                  };

                  return (
                    <MenuItem
                      key={menu.label}
                      onClick={handleMenuItemClick}
                      height="10"
                      closeOnSelect={!menu.showSwitch}
                      fontSize="sm"
                      color={menuTextColor}
                    >
                      <Flex justifyContent="space-between" grow={1}>
                        <Flex pointerEvents="none" align="center">
                          <Icon
                            mr={2}
                            w="18px"
                            h="18px"
                            display="flex"
                            alignItems="center"
                            color="gray.500"
                            as={menu.icon}
                          />
                          {menu.label}
                        </Flex>
                        {menu.showSwitch && (
                          // MenuItem renders as <button>; the wrapping span drives
                          // the toggle (and stops the row's onClick from also
                          // firing) while the switch itself stays a purely visual,
                          // non-focusable indicator so the press is handled once.
                          <Box
                            as="span"
                            onClick={(event: React.MouseEvent<HTMLSpanElement>) => {
                              event.stopPropagation();
                              menu.onSwitchChange?.();
                            }}
                          >
                            <Box as="span" display="inline-flex" pointerEvents="none">
                              <Switch
                                size="sm"
                                isSelected={!!menu.isSwitchChecked}
                                excludeFromTabOrder
                                aria-label={menu.label}
                              />
                            </Box>
                          </Box>
                        )}
                      </Flex>
                    </MenuItem>
                  );
                })}
              </MenuList>
            )}
          </Menu>
        )}
      </Flex>
    </Flex>
  );
};
